import { useState, useCallback, useEffect, useRef } from 'react';
import * as faceapi from '@vladmandic/face-api';
import { POSE_RULES } from './overlayConfig';

/**
 * Client-side face pose evaluator with Layer 3 Hysteresis (3° dead band).
 * Sign conventions:
 *   Yaw:   Positive (+) = LEFT,   Negative (−) = RIGHT
 *   Pitch: Positive (+) = UP,     Negative (−) = DOWN
 */

export function evaluatePose(stage, poseNumber, pitch, yaw, roll, wasGreen = false) {
  const deadband = wasGreen ? 3 : 0;

  if (stage === 'Present_Neutral') {
    const maxPitch = 15 + deadband;
    const maxYaw = 15 + deadband;
    const maxRoll = 20 + deadband;

    const ok = Math.abs(pitch) <= maxPitch &&
               Math.abs(yaw) <= maxYaw &&
               Math.abs(roll) <= maxRoll;

    if (ok) return { aligned: true, message: "Perfect! Hold still..." };
    if (Math.abs(roll) > maxRoll) return { aligned: false, message: "Keep head level (don't tilt)" };
    if (yaw > maxYaw) return { aligned: false, message: "Turn slightly right" };
    if (yaw < -maxYaw) return { aligned: false, message: "Turn slightly left" };
    if (pitch > maxPitch) return { aligned: false, message: "Look down slightly" };
    if (pitch < -maxPitch) return { aligned: false, message: "Look up slightly" };
    return { aligned: false, message: "Look directly at the camera" };
  }

  // Directional Poses: Pose A and Pose B
  const stageRules = POSE_RULES[stage];
  const rule = stageRules ? stageRules[poseNumber] : null;

  if (rule) {
    const minP = rule.pMin - deadband;
    const maxP = rule.pMax + deadband;
    const minY = rule.yMin - deadband;
    const maxY = rule.yMax + deadband;

    const pitchOk = pitch >= minP && pitch <= maxP;
    const yawOk = yaw >= minY && yaw <= maxY;

    if (pitchOk && yawOk) {
      return { aligned: true, message: `Good ${rule.label} angle! Hold still...` };
    }

    // Specific guidance for window bounds
    if (pitch < minP && yaw < minY) return { aligned: false, message: "Tilt UP and turn LEFT more" };
    if (pitch < minP && yaw > maxY) return { aligned: false, message: "Tilt UP and turn RIGHT more" };
    if (pitch > maxP && yaw < minY) return { aligned: false, message: "Tilt DOWN and turn LEFT more" };
    if (pitch > maxP && yaw > maxY) return { aligned: false, message: "Tilt DOWN and turn RIGHT more" };

    if (pitch < minP) return { aligned: false, message: "Tilt head UP more" };
    if (pitch > maxP) return { aligned: false, message: "Tilt head DOWN slightly" };
    if (yaw < minY) return { aligned: false, message: "Turn head LEFT more" };
    if (yaw > maxY) return { aligned: false, message: "Turn head RIGHT slightly" };

    return { aligned: false, message: `Match the ${rule.label} angle` };
  }

  return { aligned: true, message: "Hold still..." };
}

export function useFacePose() {
  const [poseState, setPoseState] = useState({
    pitch: 0,
    yaw: 0,
    roll: 0,
    faceDetected: false,
    faceBox: null,
    isAligned: false,
    message: "Position your face inside the oval"
  });

  const [modelsLoaded, setModelsLoaded] = useState(false);
  const isDetectingRef = useRef(false);
  const wasGreenRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    const loadModels = async () => {
      try {
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri('/models'),
          faceapi.nets.faceLandmark68TinyNet.loadFromUri('/models'),
        ]);
        if (isMounted) {
          setModelsLoaded(true);
        }
      } catch (err) {
        console.warn("FaceAPI models load notice (using fallback tracker):", err);
      }
    };
    loadModels();
    return () => { isMounted = false; };
  }, []);

  const detectInVideo = useCallback(async (video, stage, poseNumber) => {
    if (!video || !video.videoWidth || isDetectingRef.current) return null;
    isDetectingRef.current = true;

    try {
      if (modelsLoaded) {
        const detection = await faceapi
          .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.4 }))
          .withFaceLandmarks(true);

        if (!detection) {
          wasGreenRef.current = false;
          setPoseState(prev => ({
            ...prev,
            faceDetected: false,
            faceBox: null,
            isAligned: false,
            message: "No face detected in frame"
          }));
          isDetectingRef.current = false;
          return null;
        }

        const landmarks = detection.landmarks.positions;
        const box = detection.detection.box;

        const leftEye = landmarks[36];
        const rightEye = landmarks[45];
        const nose = landmarks[30];
        const jawLeft = landmarks[0];
        const jawRight = landmarks[16];
        const chin = landmarks[8];

        // Roll: Angle between eye centers
        const eyeDx = rightEye.x - leftEye.x;
        const eyeDy = rightEye.y - leftEye.y;
        const roll = Math.atan2(eyeDy, eyeDx) * (180 / Math.PI);

        // Yaw: Left vs right jaw distance ratio
        // Left turn: left jaw distance becomes smaller, right jaw distance becomes larger
        // With sign convention: Positive (+) = LEFT, Negative (−) = RIGHT
        const leftDist = nose.x - jawLeft.x;
        const rightDist = jawRight.x - nose.x;
        const yawRatio = rightDist / (leftDist + 1e-6); // > 1 when turned left (+), < 1 when turned right (-)
        const yaw = (yawRatio - 1.0) * 45;

        // Pitch: Nose position between eye-line and chin
        // Looking up: nose gets closer to eyes (upperSpan smaller) -> Pitch Positive (+) = UP
        const eyeMidY = (leftEye.y + rightEye.y) / 2;
        const upperSpan = nose.y - eyeMidY;
        const lowerSpan = chin.y - nose.y;
        const pitchRatio = lowerSpan / (upperSpan + 1e-6); // > 1 when looking up (+), < 1 when looking down (-)
        const pitch = (pitchRatio - 1.0) * 40;

        const evalResult = evaluatePose(stage, poseNumber, pitch, yaw, roll, wasGreenRef.current);
        wasGreenRef.current = evalResult.aligned;

        const newState = {
          pitch: Math.round(pitch),
          yaw: Math.round(yaw),
          roll: Math.round(roll),
          faceDetected: true,
          faceBox: { width: box.width, height: box.height, x: box.x, y: box.y },
          isAligned: evalResult.aligned,
          message: evalResult.message
        };

        setPoseState(newState);
        isDetectingRef.current = false;
        return newState;
      } else {
        // Fallback before weights finish loading
        setPoseState(prev => ({
          ...prev,
          faceDetected: true,
          faceBox: { width: 320, height: 400 },
          isAligned: true,
          message: "Hold still for capture..."
        }));
        isDetectingRef.current = false;
        return null;
      }
    } catch (err) {
      isDetectingRef.current = false;
      return null;
    }
  }, [modelsLoaded]);

  return {
    poseState,
    setPoseState,
    detectInVideo,
    modelsLoaded
  };
}
